'use client';

import React from "react"

import { useEffect, useRef, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog"

import { Button } from "./ui/button"
import { ZoomIn, ZoomOut, RotateCcw, Move, X } from "lucide-react"

export function ModalImageViewer() {
    const [open, setOpen] = useState(false)
    const [scale, setScale] = useState(1)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
    const [imageSrc, setImageSrc] = useState("")
    const containerRef = useRef<HTMLDivElement>(null)

    const MIN_SCALE = 0.5
    const MAX_SCALE = 5
    const ZOOM_STEP = 0.25

    useEffect(() => {
        const fn = (e: Event) => {
            const customEvent = e as CustomEvent<{ src?: string }>
            if (customEvent.detail?.src) {
                setImageSrc(customEvent.detail.src)
            }
            setOpen(true)
        }
        window.addEventListener("open-annotation-modal", fn)
        return () => window.removeEventListener("open-annotation-modal", fn)
    }, [])

    const handleReset = useCallback(() => {
        setScale(1)
        setPosition({ x: 0, y: 0 })
    }, [])

    const handleZoomIn = useCallback(() => {
        setScale(prev => Math.min(prev + ZOOM_STEP, MAX_SCALE))
    }, [])

    const handleZoomOut = useCallback(() => {
        setScale(prev => Math.max(prev - ZOOM_STEP, MIN_SCALE))
    }, [])

    const handleWheel = useCallback((e: React.WheelEvent) => {
        //e.preventDefault()
        
        if (!containerRef.current) return
        
        const rect = containerRef.current.getBoundingClientRect()
        const mouseX = e.clientX - rect.left - rect.width / 2
        const mouseY = e.clientY - rect.top - rect.height / 2
        
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
        
        setScale(prevScale => {
            const newScale = Math.min(Math.max(prevScale + delta, MIN_SCALE), MAX_SCALE)
            
            if (newScale !== prevScale) {
                const scaleFactor = newScale / prevScale
                
                setPosition(prevPos => ({
                    x: mouseX - (mouseX - prevPos.x) * scaleFactor,
                    y: mouseY - (mouseY - prevPos.y) * scaleFactor
                }))
            }
            
            return newScale
        })
    }, [])

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (scale > 1) {
            setIsDragging(true)
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
        }
    }, [scale, position])

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            })
        }
    }, [isDragging, dragStart])

    const handleMouseUp = useCallback(() => {
        setIsDragging(false)
    }, [])

    const handleOpenChange = useCallback((isOpen: boolean) => {
        setOpen(isOpen)
        if (!isOpen) {
            handleReset()
        }
    }, [handleReset])

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {/**<DialogTrigger asChild>
                <Button>Abrir modal</Button>
            </DialogTrigger> */}

            <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0 flex flex-col overflow-hidden">
                <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <DialogTitle>Visualizador de Imagem</DialogTitle>
                        <div className="flex items-center gap-1 mr-20">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleZoomOut}
                                disabled={scale <= MIN_SCALE}
                                title="Diminuir zoom"
                            >
                                <ZoomOut className="h-4 w-4" />
                            </Button>
                            <div className="px-2 min-w-[60px] text-center text-sm font-medium">
                                {Math.round(scale * 100)}%
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleZoomIn}
                                disabled={scale >= MAX_SCALE}
                                title="Aumentar zoom"
                            >
                                <ZoomIn className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleReset}
                                title="Resetar visualização"
                            >
                                <RotateCcw className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </DialogHeader>

                <div
                    ref={containerRef}
                    className="flex-1 overflow-hidden bg-muted/50 relative"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                >
                    <div className="absolute inset-0 flex items-center justify-center">
                        <img
                            id="annotation-preview"
                            src={imageSrc || "/placeholder.svg"}
                            alt="Preview"
                            className="max-w-full max-h-full object-contain select-none"
                            style={{
                                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            }}
                            draggable={false}
                        />
                    </div>
                    
                    {scale > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-muted-foreground flex items-center gap-1.5">
                            <Move className="h-3 w-3" />
                            Arraste para mover
                        </div>
                    )}
                </div>

                <div className="p-3 border-t flex-shrink-0 flex justify-between items-center bg-background">
                    <p className="text-xs text-muted-foreground">
                        Use a roda do mouse para zoom ou os controles acima
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                    >
                        <X className="h-4 w-4 mr-2" />
                        Fechar
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
